import { Controller } from "@hotwired/stimulus";
import interact from "interactjs";
import { animate } from "animejs";

const SNAP_DIST = 10; // px — snap trigger radius
const GAP = 5; // px — enforced gap between snapped edges
const MIN_W = 160; // px — minimum panel width
const MIN_H = 44; // px — minimum panel height (header-only when collapsed)
const SAVE_DELAY = 600; // ms — debounce for server save

export default class DashboardCanvasController extends Controller {
  static targets = ["canvas"];
  static values = {
    layout: { type: Object, default: {} },
    defaultLayout: { type: Object, default: {} },
    reflow: { type: Boolean, default: false },
    readOnly: { type: Boolean, default: false },
  };

  layoutValueChanged() {
    if (this._canvasW > 0) this._applyLayout();
  }

  connect() {
    this._zMax = 100;
    this._saveTimer = null;
    this._pendingSave = false;
    this._saving = false;
    this._resavePending = false;
    this._resetPending = false;
    this._state = {}; // panelId → { left, top, width, height, locked, z }
    this._canvasW = 0;
    this._canvasH = 0;
    this._consumeOptimisticGlobals();

    // Whether this connect needs an initial _reflowAutoColumns pass.
    // True for cold connects (no stale snapshot) where panels need stacking from
    // server-rendered y_pct positions. False after an optimistic preset apply —
    // the outgoing controller already stacked panels before the Turbo Frame swap,
    // and _stayingPositions holds the correct pixel values to restore.
    const hadStayingPanels = !!this._stayingPanels;
    this._reflowPending = !hadStayingPanels;

    this._applyLayout();

    // _applyLayout returns early when the canvas has no height yet (offsetHeight
    // is 0 until DashboardHeightController runs, which is after this connect).
    // In that case immediately place staying panels at their pre-swap pixel
    // positions to avoid a blank-canvas flash while waiting for _onCanvasResize
    // to trigger the real layout pass.
    if ((this._canvasW === 0 || this._canvasH === 0) && this._stayingPanels?.size) {
      this._panels().forEach((panel) => {
        const id = panel.dataset.id;
        if (!this._stayingPanels.has(id)) return;
        panel.style.transition = "none";
        const pos = this._stayingPositions?.[id];
        if (pos) {
          if (pos.left) panel.style.left = pos.left;
          if (pos.top) panel.style.top = pos.top;
          if (pos.width) panel.style.width = pos.width;
          if (pos.height) panel.style.height = pos.height;
        }
        panel.classList.remove("opacity-0");
      });
    }

    // After an optimistic preset apply, _reflowAutoColumns(false) already ran on
    // the outgoing controller before the Turbo Frame swap, so columns are already
    // correctly stacked. Skipping the RAF here means positions are stable by the
    // time turbo:frame-render fires, preventing the T0→T3 drift caught by the
    // system spec. For a cold connect (no optimistic apply), the RAF is still
    // needed so _resolveOverlaps can read actual panel heights after first paint.
    if (!hadStayingPanels) {
      requestAnimationFrame(() => {
        this._resolveOverlaps();
      });
    }

    this._panels().forEach((panel) => this._wirePanel(panel));

    this._onToggleBound = this._onToggle.bind(this);
    this.element.addEventListener("panel:toggle", this._onToggleBound);

    this._onBeforeVisit = this._flushPendingSave.bind(this);
    document.addEventListener("turbo:before-visit", this._onBeforeVisit);
    window.addEventListener("beforeunload", this._onBeforeVisit);

    this._ro = new ResizeObserver(this._onCanvasResize.bind(this));
    this._ro.observe(this.canvasTarget);

    // Wire newly Turbo-injected panels and sync the picker when panels are added/removed.
    this._panelObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1 || !node.dataset?.id) continue;
          const merged = { ...this.defaultLayoutValue, ...this.layoutValue };
          this._wirePanel(node);
          this._applyPanelLayout(node, merged);
          this._reflowAutoColumns();
          requestAnimationFrame(() => node.classList.remove("opacity-0"));
          this._updatePickerRow(node.dataset.id, false);
        }
        for (const node of mutation.removedNodes) {
          if (node.nodeType !== 1 || !node.dataset?.id) continue;
          this._updatePickerRow(node.dataset.id, true);
        }
      }
    });
    this._panelObserver.observe(this.canvasTarget, { childList: true });
  }

  disconnect() {
    clearTimeout(this._saveTimer);
    this._ro?.disconnect();
    this._panelObserver?.disconnect();
    this._panels().forEach((p) => {
      try {
        interact(p).unset();
      } catch {
        /* not wired */
      }
    });
    this.element.removeEventListener("panel:toggle", this._onToggleBound);
    document.removeEventListener("turbo:before-visit", this._onBeforeVisit);
    window.removeEventListener("beforeunload", this._onBeforeVisit);
  }

  toggleLock(event) {
    const panel = event.target.closest("[data-id]");
    if (panel) this._toggleLock(panel);
  }

  optimisticPresetApply(event) {
    this.readOnlyValue = true;

    // Restore editability if the frame request fails (controller won't reconnect on error).
    const frame = document.getElementById("sc-dashboard");
    const restoreOnError = () => {
      this.readOnlyValue = false;
    };
    const safetyTimer = setTimeout(restoreOnError, 15000);
    frame?.addEventListener(
      "turbo:frame-missing",
      () => {
        clearTimeout(safetyTimer);
        restoreOnError();
      },
      { once: true },
    );
    frame?.addEventListener("turbo:frame-render", () => clearTimeout(safetyTimer), { once: true });

    const form = event.target;
    const layout = JSON.parse(form.dataset.presetLayout || "{}");
    const hidden = JSON.parse(form.dataset.presetHidden || "[]");

    // Tag this apply with a monotonic token so connect() ignores stale globals
    // from a superseded apply that fired before the previous Turbo response arrived.
    const token = (window._scApplyToken = (window._scApplyToken || 0) + 1);

    // Remember which panels are currently visible so they can skip the fade-in
    // when the Turbo Frame reconnects (avoids blank-canvas flicker).
    const hiddenSet = new Set(hidden);
    window._scStayingPanels = new Set(
      [...this.canvasTarget.querySelectorAll("[data-id]")]
        .map((p) => p.dataset.id)
        .filter((id) => !hiddenSet.has(id)),
    );
    window._scStayingToken = token;

    // Snapshot the pixel positions of staying panels so connect() can restore them
    // immediately after the Turbo Frame swap, before the ResizeObserver fires.
    // Without this, panels sit at their server-rendered y_pct inline styles for
    // 1–2 frames while the browser measures the canvas — causing the canvas to flash.
    window._scStayingPositions = {};
    for (const id of window._scStayingPanels) {
      const p = this.canvasTarget.querySelector(`[data-id="${id}"]`);
      if (p) {
        window._scStayingPositions[id] = {
          left: p.style.left,
          top: p.style.top,
          width: p.style.width,
          height: p.style.height,
        };
      }
    }

    // Hand off the current canvas dimensions so connect() can run _applyLayout()
    // immediately with real pixel dimensions rather than waiting for the ResizeObserver.
    // The canvas size never changes during a preset switch, so this is always safe.
    window._scCanvasSize = {
      width: this.canvasTarget.offsetWidth,
      height: this.canvasTarget.offsetHeight,
    };

    // Fade out then remove panels the preset excludes
    hidden.forEach((panelId) => {
      const panel = this.canvasTarget.querySelector(`[data-id="${panelId}"]`);
      if (!panel) return;
      try {
        interact(panel).unset();
      } catch {
        /* not wired */
      }
      delete this._state[panelId];
      this._fadeAndRemove(panel);
    });

    // Snapshot positions of remaining panels before applying the new layout,
    // then animate from old to new once heights are measurable in the next RAF.
    const snapshots = new Map();
    this._panels().forEach((p) => {
      const id = p.dataset.id;
      if (id && this._state[id]) {
        snapshots.set(id, {
          left: parseFloat(p.style.left) || 0,
          top: parseFloat(p.style.top) || 0,
          width: parseFloat(p.style.width) || 0,
        });
      }
    });

    this.layoutValue = layout;

    // Heights are fresh in the next RAF: reflow columns silently (anime handles
    // the actual animation), then kick off position transitions.
    requestAnimationFrame(() => {
      this._reflowAutoColumns(false);
      this._animateFromSnapshots(snapshots);
    });
  }

  reflowColumns() {
    this._reflowAutoColumns();
    this._scheduleSave();
  }

  // Nudge any panel that has drifted outside the canvas back to (0,0).
  // Triggered by the "Bring into view" toolbar button.
  bringIntoView() {
    const cW = this.canvasTarget.offsetWidth;
    const cH = this.canvasTarget.offsetHeight;
    let moved = false;
    this._panels().forEach((panel) => {
      const id = this._panelId(panel);
      if (!id || !this._state[id]) return;
      const s = this._state[id];
      const newL = Math.max(0, Math.min(s.left, cW - s.width));
      const newT = Math.max(0, Math.min(s.top, cH - (s.height || panel.offsetHeight)));
      if (newL !== s.left || newT !== s.top) {
        s.left = newL;
        s.top = newT;
        panel.style.left = newL + "px";
        panel.style.top = newT + "px";
        moved = true;
      }
    });
    if (moved) this._scheduleSave();
  }

  resetLayout(event) {
    const msg = event.currentTarget.dataset.confirm;
    const doReset = () => {
      this.layoutValue = {};
      this._zMax = 100;
      this._applyLayout();
      clearTimeout(this._saveTimer);
      this._saveTimer = null;
      this._pendingSave = false;
      // A queued resave would re-send the pre-reset layout right after the
      // reset PATCH settles, silently un-resetting it. The reset always wins.
      this._resavePending = false;
      this._sendResetPatch();
    };
    if (msg && window.Turbo?.config?.confirmMethod) {
      window.Turbo.config.confirmMethod(msg, event.currentTarget, null).then((ok) => {
        if (ok) doReset();
      });
    } else {
      doReset();
    }
  }

  // Same in-flight guard as _performSave(): if a debounced layout save is
  // still in flight, its response could resolve after this reset PATCH and
  // silently restore the stale pre-reset positions. Defer until it settles.
  _sendResetPatch() {
    if (this._saving) {
      this._resetPending = true;
      return;
    }
    this._saving = true;
    fetch("/api/v1/user_preferences/canvas_layout", {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
      body: JSON.stringify({ layout: {} }),
    })
      .then((r) => this._reportSaveFailure(r))
      .catch((e) => this._reportSaveFailure(e))
      .finally(() => {
        this._saving = false;
      });
  }

  // Immediately removes the panel from the canvas, then persists the change.
  // The MutationObserver handles updating the picker row once removal is detected.
  hidePanel(event) {
    const panelId = event.currentTarget.dataset.panelId;
    const panel = this.canvasTarget.querySelector(`[data-id="${panelId}"]`);
    if (panel) {
      try {
        interact(panel).unset();
      } catch {
        /* interact may not know this panel */
      }
      delete this._state[panelId];
      this._fadeAndRemove(panel);
    }
    this._persistVisibility(panelId, true);
  }

  _fadeAndRemove(panel) {
    animate(panel, {
      opacity: 0,
      duration: 200,
      ease: "outQuad",
      onComplete: () => panel.remove(),
    });
  }

  _animateFromSnapshots(snapshots) {
    this._panels().forEach((p) => {
      const id = p.dataset.id;
      const old = snapshots.get(id);
      const s = this._state[id];
      if (!old || !s) return;

      const dl = Math.abs(s.left - old.left);
      const dt = Math.abs(s.top - old.top);
      const dw = Math.abs(s.width - old.width);
      if (dl < 1 && dt < 1 && dw < 1) return;

      // Restore pre-apply position so anime can animate from it.
      p.style.left = old.left + "px";
      p.style.top = old.top + "px";
      p.style.width = old.width + "px";

      const targets = {};
      if (dl >= 1) targets.left = s.left;
      if (dt >= 1) targets.top = s.top;
      if (dw >= 1) targets.width = s.width;

      animate(p, { ...targets, duration: 200, ease: "outCubic" });
    });
  }

  _persistVisibility(panelId, hidden) {
    fetch("/api/v1/user_preferences/panel_visibility", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content ?? "",
      },
      body: JSON.stringify({ panel_id: panelId, hidden }),
    });
  }

  // Toggles the show/hide buttons inside a picker row to match the panel's hidden state.
  _updatePickerRow(panelId, isHidden) {
    const row = document.getElementById(`picker-row-${panelId}`);
    if (!row) return;
    row.querySelector("[data-picker-show]")?.classList.toggle("hidden", !isHidden);
    row.querySelector("[data-picker-hide]")?.classList.toggle("hidden", isHidden);
    this._updatePanelCountBadge();
  }

  _updatePanelCountBadge() {
    const badge = document.getElementById("panel-count-badge");
    if (!badge) return;
    const rows = document.querySelectorAll("[id^='picker-row-']");
    const hiddenCount = [...rows].filter(
      (r) => !r.querySelector("[data-picker-show]")?.classList.contains("hidden"),
    ).length;
    badge.textContent = `${rows.length - hiddenCount}/${rows.length}`;
  }

  // ─── Layout initialisation ───────────────────────────────────────────────

  _inheritedDim(axis) {
    return this._inheritedSize ? this._inheritedSize[axis] : 0;
  }

  _applyLayout() {
    this._canvasW = this.canvasTarget.offsetWidth || this._inheritedDim("width");
    // Do not fall back for height. offsetHeight is 0 until DashboardHeightController
    // runs (after this connect). Keeping _canvasH = 0 here ensures _onCanvasResize
    // triggers a full _applyLayout() pass with the real height rather than using a
    // stale estimate that causes a proportional-scale jump.
    this._canvasH = this.canvasTarget.offsetHeight;
    if (this._canvasW === 0 || this._canvasH === 0) return;

    const merged = { ...this.defaultLayoutValue, ...this.layoutValue };
    const staying = this._stayingPanels;
    this._stayingPanels = null;

    this._panels().forEach((panel) => {
      const id = panel.dataset.id;
      if (staying?.has(id)) {
        // Suppress CSS transitions before repositioning so the opacity flip doesn't animate.
        panel.style.transition = "none";
      }
      this._applyPanelLayout(panel, merged);
      if (staying?.has(id)) {
        // Make staying panels visible immediately — no fade-in, no blank frame.
        panel.classList.remove("opacity-0");
      }
    });

    // Run the auto-column reflow exactly once — on the first real-dimension layout
    // pass after a cold connect. _reflowPending is false after an optimistic preset
    // apply so staying panels keep their pre-swap positions and don't animate.
    if (this.reflowValue && this._reflowPending) {
      this._reflowAutoColumns(true);
      this._scheduleSave();
      this._reflowPending = false;
    }

    // Restore transitions for staying panels and fade in new panels.
    requestAnimationFrame(() => {
      this._panels().forEach((p) => {
        if (staying?.has(p.dataset.id)) {
          p.style.removeProperty("transition");
        } else {
          p.classList.remove("opacity-0");
        }
      });
    });
  }

  // After layout load, nudge any auto-height panel that overlaps the one above
  // it in the same column. Only the overlapping panel moves — everything else
  // keeps its saved position unchanged.
  _resolveOverlaps() {
    const COL_TOL = 50;
    const GAP = 8;

    const panels = this._panels().filter((p) => {
      const id = this._panelId(p);
      return id && this._state[id]?.autoHeight;
    });

    const cols = new Map();
    panels.forEach((p) => {
      const key = Math.round(this._state[this._panelId(p)].left / COL_TOL);
      if (!cols.has(key)) cols.set(key, []);
      cols.get(key).push(p);
    });

    let changed = false;
    cols.forEach((col) => {
      col.sort((a, b) => this._state[this._panelId(a)].top - this._state[this._panelId(b)].top);
      for (let i = 1; i < col.length; i++) {
        const prevId = this._panelId(col[i - 1]);
        const currId = this._panelId(col[i]);
        const minTop = this._state[prevId].top + col[i - 1].offsetHeight + GAP;
        if (this._state[currId].top < minTop) {
          this._state[currId].top = minTop;
          col[i].style.top = minTop + "px";
          changed = true;
        }
      }
    });

    if (changed) this._scheduleSave();
  }

  // Stack auto-height panels in each column using their actual rendered heights.
  // animated=false skips the slide transition — used by the post-connect reflow
  // that runs after a Turbo Frame preset apply, where the server resets panels
  // to hardcoded y_pct values and we just need to silently correct them back.
  _reflowAutoColumns(animated = true) {
    const GAP = 8;
    const COL_TOL = 50;

    const items = this._panels()
      .map((panel) => {
        const id = this._panelId(panel);
        if (!id || !this._state[id]) return null;
        if (!this._state[id].autoHeight) return null;
        return { id, panel, s: this._state[id], h: panel.offsetHeight };
      })
      .filter(Boolean);

    const cols = new Map();
    items.forEach((item) => {
      const key = Math.round(item.s.left / COL_TOL);
      if (!cols.has(key)) cols.set(key, []);
      cols.get(key).push(item);
    });

    if (!cols.size) return;

    const moves = [];
    cols.forEach((col) => {
      col.sort((a, b) => a.s.top - b.s.top);
      let cursor = col[0].s.top;
      col.forEach((item) => {
        if (Math.abs(item.s.top - cursor) > 1) moves.push({ item, top: cursor });
        cursor += item.h + GAP;
      });
    });

    if (!moves.length) return;

    moves.forEach(({ item, top }) => {
      item.s.top = top;
      if (animated) {
        animate(item.panel, { top, duration: 200, ease: "outCubic" });
      } else {
        item.panel.style.top = top + "px";
      }
    });
  }

  _applyPanelLayout(panel, merged) {
    const id = this._panelId(panel);
    if (!id) return;

    const data = merged[id] || {};
    const def = this.defaultLayoutValue[id] || {};
    const version = merged._version || 0;
    const hasH = this._hasFixedHeight(data, def, version);
    const l = ((data.x_pct ?? 0) / 100) * this._canvasW;
    const t = ((data.y_pct ?? 0) / 100) * this._canvasH;
    const w = ((data.w_pct ?? 33) / 100) * this._canvasW;
    const h = hasH ? (data.h_pct / 100) * this._canvasH : null;
    const z = data.z || ++this._zMax;
    const locked = !!data.locked;

    this._state[id] = { left: l, top: t, width: w, height: h, autoHeight: !hasH, locked, z };
    this._setPanelStyle(panel, l, t, w, h, hasH);
    panel.style.zIndex = z;
    this._zMax = Math.max(this._zMax, z);
    panel.classList.toggle("sc-panel-locked", locked);
    this._updateLockBtn(panel, locked);
  }

  _setPanelStyle(panel, l, t, w, h, hasH) {
    if (hasH) {
      panel.classList.remove("sc-panel-auto");
      this._applyStyle(panel, l, t, w, h);
    } else {
      panel.classList.add("sc-panel-auto");
      panel.style.left = l + "px";
      panel.style.top = t + "px";
      panel.style.width = w + "px";
      panel.style.height = "";
    }
  }

  _applyStyle(panel, l, t, w, h) {
    panel.style.left = l + "px";
    panel.style.top = t + "px";
    panel.style.width = w + "px";
    panel.style.height = h + "px";
  }

  // ─── Panel wiring ─────────────────────────────────────────────────────────

  _wirePanel(panel) {
    const id = this._panelId(panel);
    if (!id) return;

    panel.addEventListener(
      "pointerdown",
      (e) => {
        panel._scDownTarget = e.target;
        this._raise(panel);
      },
      { capture: true },
    );
    this._wireInteract(panel);
  }

  _wireInteract(panel) {
    const id = this._panelId(panel);

    interact(panel)
      .draggable({
        handle: ".sc-drag-handle",
        ignoreFrom: "button, a, input, select, textarea",
        cursorChecker: () => null,
        listeners: {
          start: (evt) => {
            this._suppressSelection();
            this._onDragStart(evt, panel, id);
          },
          move: (evt) => {
            if (this._state[id]?.locked) return;
            const el = evt.target;
            el._rawL = (el._rawL ?? 0) + evt.dx;
            el._rawT = (el._rawT ?? 0) + evt.dy;

            const [sl, st] = this._snap(
              id,
              el._rawL,
              el._rawT,
              parseFloat(el.style.width),
              el.offsetHeight,
            );
            el.style.left = sl + "px";
            el.style.top = st + "px";
            this._updateGuides(id, sl, st, parseFloat(el.style.width), el.offsetHeight);
          },
          end: (evt) => {
            this._restoreSelection();
            this._clearGuides();
            const el = evt.target;
            const l = parseFloat(el.style.left);
            const t = parseFloat(el.style.top);
            if (this._state[id]) {
              this._state[id].left = l;
              this._state[id].top = t;
            }
            this._scheduleSave();
          },
        },
      })
      .resizable({
        edges: { left: true, right: true, bottom: true },
        margin: 8,
        modifiers: [interact.modifiers.restrictSize({ min: { width: MIN_W, height: MIN_H } })],
        listeners: {
          start: (evt) => {
            if (this._state[id]?.locked) {
              evt.interaction.stop();
              return;
            }
            this._suppressSelection();
            const el = evt.target;
            el._resizeBase = { left: parseFloat(el.style.left), top: parseFloat(el.style.top) };
            el._minResizeH = id !== "live-chat" ? el.scrollHeight : MIN_H;
          },
          move: (evt) => {
            if (this._state[id]?.locked) return;
            const el = evt.target;
            const base = el._resizeBase || { left: 0, top: 0 };
            el.style.left = base.left + evt.deltaRect.left + "px";
            el.style.top = base.top + evt.deltaRect.top + "px";
            el.style.width = evt.rect.width + "px";
            el.style.height = Math.max(evt.rect.height, el._minResizeH ?? MIN_H) + "px";
            el._resizeBase = {
              left: base.left + evt.deltaRect.left,
              top: base.top + evt.deltaRect.top,
            };
          },
          end: (evt) => {
            this._restoreSelection();
            const el = evt.target;
            el._resizeBase = null;
            const l = parseFloat(el.style.left);
            const t = parseFloat(el.style.top);
            const w = parseFloat(el.style.width);
            const h = parseFloat(el.style.height);
            if (this._state[id]) {
              this._state[id].left = l;
              this._state[id].top = t;
              this._state[id].width = w;
              this._state[id].height = h;
              this._state[id].autoHeight = false;
              el.classList.remove("sc-panel-auto");
            }
            this._scheduleSave();
          },
        },
      });
  }

  // ─── Snap logic ───────────────────────────────────────────────────────────

  _snap(movedId, rawL, rawT, w, h) {
    const cW = this.canvasTarget.offsetWidth;
    const cH = this.canvasTarget.offsetHeight;

    let bestL = rawL,
      bestT = rawT;
    let dxBest = Infinity,
      dyBest = Infinity;

    const tryX = (edge, target, newL) => {
      const d = Math.abs(edge - target);
      if (d < SNAP_DIST && d < Math.abs(dxBest)) {
        bestL = newL;
        dxBest = edge - target;
      }
    };
    const tryY = (edge, target, newT) => {
      const d = Math.abs(edge - target);
      if (d < SNAP_DIST && d < Math.abs(dyBest)) {
        bestT = newT;
        dyBest = edge - target;
      }
    };

    // Canvas boundary snaps
    tryX(rawL, 0, 0);
    tryX(rawL + w, cW, cW - w);
    tryY(rawT, 0, 0);
    tryY(rawT + h, cH, cH - h);

    // Other panel edge snaps
    for (const [otherId, s] of Object.entries(this._state)) {
      if (otherId === movedId) continue;
      const ol = s.left,
        ot = s.top,
        ow = s.width;
      const oPanel = this.canvasTarget.querySelector(`[data-id="${otherId}"]`);
      const oh = oPanel ? oPanel.offsetHeight : s.height;
      const or = ol + ow,
        ob = ot + oh;

      tryX(rawL, ol, ol);
      tryX(rawL, or + GAP, or + GAP);
      tryX(rawL + w, or, or - w);
      tryX(rawL + w, ol - GAP, ol - GAP - w);

      tryY(rawT, ot, ot);
      tryY(rawT, ob + GAP, ob + GAP);
      tryY(rawT + h, ob, ob - h);
      tryY(rawT + h, ot - GAP, ot - GAP - h);
    }

    // Keep panels fully within the canvas bounds.
    bestL = Math.max(0, Math.min(bestL, cW - w));
    bestT = Math.max(0, Math.min(bestT, cH - h));

    return [bestL, bestT];
  }

  // ─── Snap guide lines ─────────────────────────────────────────────────────

  _updateGuides(movedId, l, t, w, h) {
    this._clearGuides();
    const fragment = document.createDocumentFragment();

    for (const [otherId, s] of Object.entries(this._state)) {
      if (otherId === movedId) continue;
      const ol = s.left,
        ow = s.width;
      const oPanel = this.canvasTarget.querySelector(`[data-id="${otherId}"]`);
      const oh = oPanel ? oPanel.offsetHeight : s.height;
      const or = ol + ow,
        ob = s.top + oh;

      const addX = (x) => {
        const g = this._guide("x");
        g.style.left = x + "px";
        fragment.append(g);
      };
      const addY = (y) => {
        const g = this._guide("y");
        g.style.top = y + "px";
        fragment.append(g);
      };

      if (Math.abs(l - ol) < SNAP_DIST) addX(ol);
      if (Math.abs(l + w - or) < SNAP_DIST) addX(or);
      if (Math.abs(t - s.top) < SNAP_DIST) addY(s.top);
      if (Math.abs(t + h - ob) < SNAP_DIST) addY(ob);
    }

    if (fragment.childNodes.length) {
      this._guideContainer = document.createElement("div");
      this._guideContainer.style.cssText =
        "position:absolute;inset:0;pointer-events:none;z-index:9999;overflow:hidden;";
      this._guideContainer.append(fragment);
      this.canvasTarget.append(this._guideContainer);
    }
  }

  _guide(axis) {
    const el = document.createElement("div");
    el.className = `sc-guide sc-guide-${axis}`;
    return el;
  }

  _clearGuides() {
    this._guideContainer?.remove();
    this._guideContainer = null;
  }

  // ─── Raise (z-index) ──────────────────────────────────────────────────────

  _raise(panel) {
    const id = this._panelId(panel);
    const z = ++this._zMax;
    panel.style.zIndex = z;
    if (this._state[id]) this._state[id].z = z;
  }

  // ─── Lock ─────────────────────────────────────────────────────────────────

  _toggleLock(panel) {
    const id = this._panelId(panel);
    if (!this._state[id]) return;

    const locked = !this._state[id].locked;
    this._state[id].locked = locked;
    panel.classList.toggle("sc-panel-locked", locked);
    this._updateLockBtn(panel, locked);

    if (locked) {
      interact(panel).draggable(false).resizable(false);
    } else {
      this._wireInteract(panel);
    }

    this._scheduleSave();
  }

  _updateLockBtn(panel, locked) {
    const btn = panel.querySelector(".sc-lock-btn");
    if (!btn) return;
    btn.textContent = locked ? "🔒" : "🔓";
    btn.setAttribute(
      "aria-label",
      locked ? btn.dataset.lockLockedLabel || "" : btn.dataset.lockUnlockedLabel || "",
    );
  }

  // ─── Collapse BFS reflow ──────────────────────────────────────────────────

  _onToggle(event) {
    const panel = event.target.closest("[data-id]");
    if (!panel) return;

    const { originalBottom, delta } = event.detail;
    if (Math.abs(delta) < 2) return;

    this._bfsReflow(panel, originalBottom, delta);
    this._scheduleSave();
  }

  _bfsReflow(originPanel, originalBottom, delta) {
    const originId = this._panelId(originPanel);
    const ol = parseFloat(originPanel.style.left);
    const ow = parseFloat(originPanel.style.width);
    const zone = SNAP_DIST + GAP + 4;

    const visited = new Set([originId]);
    const queue = this._bfsSeed(ol, ow, originalBottom, zone, visited);

    for (let i = 0; i < queue.length; i++) {
      this._bfsStep(queue[i], delta, zone, queue, visited);
    }
  }

  _bfsSeed(ol, ow, originalBottom, zone, visited) {
    const queue = [];
    for (const p of this._panels()) {
      const id = this._panelId(p);
      if (!id || visited.has(id)) continue;
      if (Math.abs(parseFloat(p.style.top) - originalBottom) > zone) continue;
      if (this._hOverlap(ol, ow, parseFloat(p.style.left), parseFloat(p.style.width)) >= 0.5) {
        queue.push(p);
        visited.add(id);
      }
    }
    return queue;
  }

  _bfsStep(p, delta, zone, queue, visited) {
    const id = this._panelId(p);
    const oldT = parseFloat(p.style.top);
    const oldB = oldT + p.offsetHeight;
    const pl = parseFloat(p.style.left);
    const pw = parseFloat(p.style.width);

    p.style.top = oldT + delta + "px";
    if (this._state[id]) this._state[id].top = oldT + delta;

    for (const p2 of this._panels()) {
      const id2 = this._panelId(p2);
      if (!id2 || visited.has(id2)) continue;
      if (Math.abs(parseFloat(p2.style.top) - oldB) > zone) continue;
      if (this._hOverlap(pl, pw, parseFloat(p2.style.left), parseFloat(p2.style.width)) >= 0.5) {
        queue.push(p2);
        visited.add(id2);
      }
    }
  }

  _onDragStart(evt, panel, id) {
    const state = this._state[id] || {};
    if (!panel._scDownTarget?.closest(".sc-drag-handle") || state.locked) {
      evt.interaction.stop();
      return;
    }
    const el = evt.target;
    el._rawL = state.left ?? parseFloat(el.style.left) ?? 0;
    el._rawT = state.top ?? parseFloat(el.style.top) ?? 0;
  }

  _hOverlap(l1, w1, l2, w2) {
    const overlap = Math.min(l1 + w1, l2 + w2) - Math.max(l1, l2);
    const minW = Math.min(w1, w2);
    return minW > 0 ? overlap / minW : 0;
  }

  // ─── Canvas resize ────────────────────────────────────────────────────────

  _onCanvasResize() {
    const newW = this.canvasTarget.offsetWidth;
    const newH = this.canvasTarget.offsetHeight;
    if (newW === 0 || newH === 0) return;
    // If either dimension was 0 before, do a full layout pass (not a scale)
    if (this._canvasW === 0 || this._canvasH === 0) {
      this._applyLayout();
      return;
    }
    if (newW === this._canvasW && newH === this._canvasH) return;

    const scaleX = newW / this._canvasW;
    const scaleY = newH / this._canvasH;

    this._panels().forEach((panel) => {
      const id = this._panelId(panel);
      if (!id || !this._state[id]) return;
      const s = this._state[id];
      s.left *= scaleX;
      s.top *= scaleY;
      s.width *= scaleX;
      if (s.autoHeight) {
        panel.style.left = s.left + "px";
        panel.style.top = s.top + "px";
        panel.style.width = s.width + "px";
      } else {
        s.height *= scaleY;
        this._applyStyle(panel, s.left, s.top, s.width, s.height);
      }
    });

    this._canvasW = newW;
    this._canvasH = newH;
  }

  // ─── Save As (snapshot current layout into a named profile) ─────────────

  onSaveAs(event) {
    const nameField = event.target.querySelector("[data-save-as-name]");
    if (nameField && !nameField.value.trim()) {
      event.preventDefault();
      nameField.classList.add("input-error");
      this._showSaveAsError(nameField);
      nameField.focus();
      return;
    }
    const field = event.target.querySelector("[data-save-as-layout]");
    if (field) field.value = JSON.stringify(this._serializeLayout());
  }

  _showSaveAsError(field) {
    document.querySelector("[data-save-as-error-bubble]")?.remove();
    const rect = field.getBoundingClientRect();
    const bubble = document.createElement("div");
    bubble.dataset.saveAsErrorBubble = "";
    bubble.className =
      "fixed z-[9999] px-2 py-1 bg-error text-error-content text-xs rounded shadow-lg pointer-events-none";
    bubble.textContent = field.dataset.errorMessage || "Required";
    bubble.popover = "manual";
    bubble.style.left = `${rect.left}px`;
    bubble.style.top = `${rect.bottom + 4}px`;
    document.body.appendChild(bubble);
    bubble.showPopover();
    const dismiss = () => {
      bubble.hidePopover();
      bubble.remove();
      field.classList.remove("input-error");
      document.removeEventListener("pointerdown", onOutside, { capture: true });
    };
    const onOutside = (e) => {
      if (e.target !== field && !bubble.contains(e.target)) dismiss();
    };
    field.addEventListener("input", dismiss, { once: true });
    setTimeout(() => document.addEventListener("pointerdown", onOutside, { capture: true }), 0);
  }

  // ─── Persist ──────────────────────────────────────────────────────────────

  _csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content ?? "";
  }

  _scheduleSave() {
    if (this.readOnlyValue) return;
    clearTimeout(this._saveTimer);
    this._pendingSave = true;
    this._saveTimer = setTimeout(() => {
      this._saveTimer = null;
      this._performSave();
    }, SAVE_DELAY);
  }

  // A save is already in flight — don't fire a second overlapping PATCH (whose
  // response could resolve first and let a stale layout overwrite a fresher one).
  // Instead, remember to immediately re-save once the in-flight one settles.
  _performSave() {
    if (this._saving) {
      this._resavePending = true;
      return;
    }
    const layout = this._serializeLayout();
    if (!layout) {
      this._pendingSave = false;
      return;
    }
    this._saving = true;
    fetch("/api/v1/user_preferences/canvas_layout", {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
      body: JSON.stringify({ layout }),
    })
      .then((r) => this._reportSaveFailure(r))
      .catch((e) => this._reportSaveFailure(e))
      .finally(() => {
        this._saving = false;
        this._pendingSave = false;
        if (this._resetPending) {
          this._resetPending = false;
          this._resavePending = false;
          this._sendResetPatch();
        } else if (this._resavePending) {
          this._resavePending = false;
          this._performSave();
        }
      });
  }

  // Flush any in-flight debounced save before page navigation or unload.
  _flushPendingSave() {
    if (!this._saveTimer && !this._pendingSave) return;
    clearTimeout(this._saveTimer);
    this._saveTimer = null;
    // A save is already in flight — firing a second concurrent PATCH here would
    // reintroduce the same stale-response race; let the in-flight one finish instead.
    if (this._saving) return;
    const layout = this._serializeLayout();
    if (!layout) return;
    // keepalive ensures the request survives page unload.
    fetch("/api/v1/user_preferences/canvas_layout", {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrfToken() },
      body: JSON.stringify({ layout }),
      keepalive: true,
    })
      .then((r) => this._reportSaveFailure(r))
      .catch((e) => this._reportSaveFailure(e));
  }

  // A failed layout save is otherwise indistinguishable from a successful one.
  // The panel has already moved -- this controller repositions optimistically and
  // updates this._state before the PATCH goes out -- so nothing on screen changes
  // and the loss only appears on the next page load. That is why every drag,
  // resize, lock and hide 500d for ten weeks without anyone noticing
  // (StreamCommand-d783). A console line is the floor here; StreamCommand-i3cp is
  // the bean for reporting this somewhere a maintainer would actually see it.
  _reportSaveFailure(response) {
    if (response instanceof Error) {
      console.warn("canvas layout save failed", response.message);
    } else if (response && !response.ok) {
      console.warn("canvas layout save failed", response.status);
    }
  }

  _serializeLayout() {
    const cW = this.canvasTarget.offsetWidth;
    const cH = this.canvasTarget.offsetHeight;
    if (cW === 0 || cH === 0) return null;

    const layout = { _version: 2 };
    this._panels().forEach((panel) => {
      const id = this._panelId(panel);
      if (!id || !this._state[id]) return;
      const s = this._state[id];
      const entry = {
        x_pct: parseFloat(((s.left / cW) * 100).toFixed(3)),
        y_pct: parseFloat(((s.top / cH) * 100).toFixed(3)),
        w_pct: parseFloat(((s.width / cW) * 100).toFixed(3)),
        locked: s.locked || false,
        z: s.z || 1,
      };
      if (!s.autoHeight) {
        entry.h_pct = parseFloat(((s.height / cH) * 100).toFixed(3));
      }
      layout[id] = entry;
    });
    return layout;
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  // Consume window globals written by optimisticPresetApply. The token guard
  // ensures a superseded apply doesn't inject stale positions into a later connect().
  _consumeOptimisticGlobals() {
    const latestToken = window._scApplyToken ?? null;
    const valid = latestToken !== null && window._scStayingToken === latestToken;
    this._stayingPanels = valid ? (window._scStayingPanels ?? null) : null;
    this._stayingPositions = valid ? (window._scStayingPositions ?? null) : null;
    this._inheritedSize = window._scCanvasSize ?? null;
    window._scStayingPanels = null;
    window._scStayingPositions = null;
    window._scCanvasSize = null;
    window._scStayingToken = null;
  }

  // v2+ layouts trust h_pct for all panels (user manually resized them).
  // Pre-v2 layouts only honour it when the default also sets h_pct (live-chat)
  // to avoid restoring stale values saved before auto-height was introduced.
  _hasFixedHeight(data, def, layoutVersion) {
    if (data.h_pct === undefined) return false;
    return layoutVersion >= 2 || def.h_pct !== undefined;
  }

  _suppressSelection() {
    document.body.style.userSelect = "none";
  }
  _restoreSelection() {
    document.body.style.userSelect = "";
  }

  _panels() {
    return Array.from(this.canvasTarget.querySelectorAll(":scope > [data-id]"));
  }

  _panelId(panel) {
    return panel.dataset.id || "";
  }
}
